// src/components/AdminCyclesTemplate.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, CheckCircle, Plus, Search, Filter, Users } from 'lucide-react';
import './AdminCycleTemplate.css';
import apiService from '../services/api'; 
import Snackbar from './Snackbar';
import { getSinglePublishEmailTemplate } from '../services/PublishEmailTemplate';
import { getBulkPublishEmailTemplate } from '../services/BulkPublishEmailTemplate';
import GlobalHeader from './GlobalHeader';

const AdminCycleTemplate = () => {
  const navigate = useNavigate();
  const [cycles, setCycles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ 
    message: '', 
    type: 'success', 
    isOpen: false 
  });

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCycle, setEditingCycle] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showBulkPublishModal, setShowBulkPublishModal] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [cycleStatus, setCycleStatus] = useState('planned');
  const [selectedManager, setSelectedManager] = useState('');
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [publishAllManagers, setPublishAllManagers] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCycle, setDeletingCycle] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [employees, setEmployees] = useState([]);        
  const [loadingEmployees, setLoadingEmployees] = useState(false); 
const [activeTab, setActiveTab] = useState('recipients');
  const [managers, setManagers] = useState([]);
const [loadingManagers, setLoadingManagers] = useState(false);
const [managerSearchTerm, setManagerSearchTerm] = useState('');
const [managerDropdownOpen, setManagerDropdownOpen] = useState(false);
const [recipientList, setRecipientList] = useState([]);
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const filteredEmployees = employees.filter(emp =>
  emp.name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
  emp.email.toLowerCase().includes(employeeSearchTerm.toLowerCase())
  );

   useEffect(() => {
     document.title = 'CR Capability Index - Admin Cycle';
   }, []);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getQuarter = (dateStr) => {
    const m = new Date(dateStr).getMonth() + 1;
    if (m >= 4 && m <= 6) return 1;
    if (m >= 7 && m <= 9) return 2;
    if (m >= 10 && m <= 12) return 3;
    return 4;
  };

  const addOneDay = (dateStr) => {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
};

  const generateAvailableCycles = () => {
    const currentYear = new Date().getFullYear();
    return [
      `FY${currentYear} C1 Assessment Cycle`,
      `FY${currentYear} C2 Assessment Cycle`
    ];
  };

  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ message, type, isOpen: true });
  };

  // === FILTERING & PAGINATION ===
  const filteredCycles = cycles.filter(cycle => {
    const matchesSearch = cycle.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cycle.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'All' || cycle.status === filterStatus.toLowerCase();
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredCycles.length / itemsPerPage);
  const paginatedCycles = filteredCycles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const total = cycles.length;
  const current = cycles.filter(c => c.status === 'current').length;
  const completed = cycles.filter(c => c.status === 'passed').length;
  const planned = cycles.filter(c => c.status === 'planned').length;
  const closed = cycles.filter(c => c.status === 'closed').length;

  // === EFFECTS ===
   const fetchCycles = async () => {
      try {
        setLoading(true);
        setError(null);

        const endpoint = '/assessment-cycles';
        const data = await apiService.fetchData(endpoint);

        if (data.status !== 'success') {
          throw new Error('API returned unsuccessful status');
        }

        const uiCycles = data.data.map(row => ({
          id: row.CycleID,
          code: row.Code,
          name: row.Name,
          startDate: row.StartsOn,
          endDate: row.EndsOn,
          status: row.Status.toLowerCase(),
          participants: Math.floor(Math.random() * 30) + 135
        }));

        setCycles(uiCycles);
      } catch (err) {
        console.error('API Error:', err);
        setError('Failed to load cycles. Please try again.');
      } finally {
        setLoading(false);
      }
    };
  useEffect(() => {
   fetchCycles();
  }, []);

// Fetch all managers on component mount
useEffect(() => {
  const fetchManagers = async () => {
    setLoadingManagers(true);
    try {
      const response = await apiService.fetchData('/get-all-managers');
      
      if (response.status === 'success' && Array.isArray(response.data)) {
        // Adjust based on your actual API response structure
        const managerEmails = response.data.map(item => 
          item.email || item.Email || item.manager_email || item
        );
        setManagers(managerEmails);
      } else {
        showSnackbar('No managers found', 'info');
      }
    } catch (err) {
      console.error('Failed to load managers:', err);
      showSnackbar('Failed to load managers', 'error');
    } finally {
      setLoadingManagers(false);
    }
  };

  fetchManagers();
}, []); 

// Close manager dropdown when clicking outside
useEffect(() => {
  if (!managerDropdownOpen) return;

  const handleClickOutside = () => {
    setManagerDropdownOpen(false);
    setManagerSearchTerm('');
  };

  document.addEventListener('click', handleClickOutside);
  return () => document.removeEventListener('click', handleClickOutside);
}, [managerDropdownOpen]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  useEffect(() => {
  const handleClickOutside = () => {
    setEmployeeSearchOpen(false);
    setEmployeeSearchTerm('');
  };
 




  if (employeeSearchOpen) {
    document.addEventListener('click', handleClickOutside);
  }

  return () => {
    document.removeEventListener('click', handleClickOutside);
  };
}, [employeeSearchOpen]);

const fetchEmployeesForManager = async (managerEmail) => {
  if (!managerEmail) {
    setEmployees([]);
    setSelectedEmployee('');
    return;
  }

  setLoadingEmployees(true);

  try {
    // ✅ POST request (backend expects this now)
    const response = await apiService.postData(
      "/get-manager",
      { manager_emails: [managerEmail],cycle_id: selectedCycle.id }   
    );

    if (
      response.status === 'success' &&
      response.data &&
      response.data.length > 0 &&
      response.data[0].employees
    ) {
      const employeeList = response.data[0].employees
  .filter(emp => emp.has_submitted === true)  // Only show employees who have submitted
  .map(emp => ({
    email: emp.Email,
    name: emp.FullName || emp.Email.split('@')[0]
  }));

      setEmployees(employeeList);
    } else {
      setEmployees([]);
      showSnackbar('No employees found for this manager', 'info');
    }

  } catch (err) {
    console.error('Failed to fetch employees:', err);
    setEmployees([]);
    showSnackbar('Failed to load employees', 'error');
  } finally {
    setLoadingEmployees(false);
  }
};

  // === HANDLERS ===
 const handleCreateCycle = async () => {
  // Validation
  if (!newStartDate || !newEndDate) {
    showSnackbar('Please select both Start Date and End Date', 'error');
    return;
  }

  if (newEndDate < newStartDate) {
    showSnackbar('End Date cannot be earlier than Start Date', 'error');
    return;
  }

  const year = newStartDate.slice(0, 4);
  const cycle = (newStartDate === `${year}-01-01` && newEndDate === `${year}-06-30`) ? 'C1' : 'C2';
  const newCode = `FY${year}-${cycle}`;
  const cycleName = `FY${year} ${cycle} Assessment Cycle`;

  const exists = cycles.some(c => c.code === newCode);
  if (exists) {
    showSnackbar(`Cycle with code ${newCode} already exists!`, 'error');
    return;
  }

  setIsPublishing(true);

  const payload = {
    StartsOn: newStartDate,
    EndsOn: newEndDate,
    Status: 'planned',
    Code: newCode,
    Name: cycleName
  };

  try {
    const endpoint = '/assessment-cycles?type=custom';
    const result = await apiService.postData(endpoint, payload);

    if (result.status !== 'success') {
      throw new Error(result.message || 'Failed to create cycle');
    }

    // SUCCESS: Close modal + show success
    setShowCreateModal(false);
    setNewStartDate('');
    setNewEndDate('');
    showSnackbar(`Cycle ${newCode} created successfully!`, 'success');

    // CRITICAL FIX: Refetch full list from server → guarantees correct CycleID
    await fetchCycles();

    window.scrollTo({ top: 0, behavior: 'smooth' });

  } catch (err) {
    console.error('Create cycle error:', err);
    showSnackbar(err.message || 'Failed to create cycle. Please try again.', 'error');
  } finally {
    setIsPublishing(false);
  }
};

  const handleDeleteClick = (cycle) => {
  setDeletingCycle(cycle);
  setShowDeleteModal(true);
};

const confirmDelete = async () => {
  if (!deletingCycle) return;

  setIsDeleting(true);
  try {
    await apiService.deleteData(`/assessment-cycles/${deletingCycle.id}`);
    showSnackbar('Assessment cycle deleted successfully!', 'success');
    setCycles(prev => prev.filter(c => c.id !== deletingCycle.id));
    setShowDeleteModal(false);
  } catch (err) {
    showSnackbar('Failed to delete cycle.', 'error');
  } finally {
    setIsDeleting(false);
    setDeletingCycle(null);
  }
};

  const openEditModal = (cycle) => {
    setEditingCycle(cycle);
    setEditStatus(cycle.status);
    setShowEditModal(true);
  };

  // const handleUpdateStatus = async () => {
  //   if (!editingCycle) return;

  //   try {
  //     const endpoint = `/assessment-cycles?cycle_id=${editingCycle.id}&status=${editStatus}`;
  //     const result = await apiService.putData(endpoint, null);

  //     if (result.status !== 'success') {
  //       throw new Error(result.message || 'Update failed');
  //     }

  //     setCycles(prev =>
  //       prev.map(c =>
  //         c.id === editingCycle.id ? { ...c, status: editStatus } : c
  //       )
  //     );

  //     setShowEditModal(false);
  //     setEditingCycle(null);
  //     showSnackbar(`Status updated to "${editStatus.toUpperCase()}"`, 'success');
  //   } catch (err) {
  //     console.error('Update error:', err);
  //     showSnackbar(err.message || 'Failed to update status', 'error');
  //   }
  // };
const handleUpdateStatus = async () => {
  if (!editingCycle || !editStatus) return;

  setIsUpdatingStatus(true); // ← Start loading

  try {
    const endpoint = `/assessment-cycles?cycle_id=${editingCycle.id}&status=${editStatus}`;
    const result = await apiService.putData(endpoint, null);

    if (result.status !== 'success') {
      throw new Error(result.message || 'Update failed');
    }

    setCycles(prev =>
      prev.map(c =>
        c.id === editingCycle.id ? { ...c, status: editStatus } : c
      )
    );

    setShowEditModal(false);
    setEditingCycle(null);
    setEditStatus('');
    showSnackbar(`Status updated to "${editStatus.toUpperCase()}"`, 'success');
  } catch (err) {
    console.error('Update error:', err);
    showSnackbar(err.message || 'Failed to update status', 'error');
  } finally {
    setIsUpdatingStatus(false); // ← Always stop loading
  }
};

  // === PUBLISH HANDLERS ===
  const openPublishModal = (cycle) => {
    setSelectedCycle(cycle);
    setSelectedManager('');
    setShowPublishModal(true);
  };

  const openBulkPublishModal = (cycle) => {
    setSelectedCycle(cycle);
    setSelectedManagers([]);
    setPublishAllManagers(false);
    setShowBulkPublishModal(true);
  };

const handlePublish = async () => {
  // Validation
  if (!selectedManager || !selectedEmployee) {
    showSnackbar('Please select both manager and employee', 'error');
    return;
  }

  // If we're in email preview mode → this is the final "Publish & Send" step
  if (showEmailPreview) {
    setIsPublishing(true); // Start loading

    const adminEmail = sessionStorage.getItem('user_emailId') || 'admin@criticalriver.com';
    const publishedOn = new Date().toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    // Format names nicely (e.g., anwar.hussain → Anwar Hussain)
    const managerName = selectedManager
      .split('@')[0]
      .replace('.', ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    const employeeName = selectedEmployee
      .split('@')[0]
      .replace('.', ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    // Generate email HTML
    // const emailHtml = getSinglePublishEmailTemplate({
    //   managerName,
    //   employeeName,
    //   cycleCode: selectedCycle.code,
    //   employeeEmail: selectedEmployee,
    //   startDate: formatDate(selectedCycle.startDate),
    //   endDate: formatDate(selectedCycle.endDate),
    //   adminEmail,
    //   publishedOn,
    //   assessmentLink: `${window.location.origin}/assessment/${selectedCycle.id}?emp=${selectedEmployee}`
    // });
    // Safely get the frontend base URL from environment variables
const FRONTEND_URL = 
window._env_.REACT_APP_FRONTEND_URL || 
 window._env_.REACT_APP_APPLICATION_URL || 
  "http://localhost:3000";

const LOGIN_URL = `${FRONTEND_URL}/login`;

// Generate the email template
const emailHtml = getSinglePublishEmailTemplate({
  managerName,
  employeeName,
  employeeEmail: selectedEmployee,
  cycleCode: selectedCycle.code,
  startDate: formatDate(selectedCycle.startDate),
  endDate: formatDate(selectedCycle.endDate),
  publishedOn,
  adminEmail,
  loginLink: LOGIN_URL, // This is always correct — local or production
  // Optional: if you ever want a direct assessment link
  assessmentLink: `${FRONTEND_URL}/assessment/${selectedCycle.id}?emp=${selectedEmployee}`,
  showViewButton: true
});

    const emailPayload = {
      to: selectedEmployee, // Email goes to EMPLOYEE
      subject: `Performance Review: ${selectedCycle.code} - Action Required`,
      html: emailHtml
    };

    try {
      console.log('Sending email to:', selectedEmployee);
      console.log('Publishing for employee:', selectedEmployee, 'under manager:', selectedManager);

      // Step 1: Send Email to Employee
      await apiService.sendEmail(emailPayload);

      // Step 2: Publish the Assessment (records manager-employee relationship)
      const publishPayload = {
        manager_email: selectedManager,
        employee_email: selectedEmployee,
        published_by: adminEmail,
        bulk_publish: false,
        cycle_id: selectedCycle.id
      };

      const result = await apiService.postData('/published', publishPayload);

      if (result.status === 'success') {
        showSnackbar(
          `Published & email sent to ${employeeName.split(' ')[0]} successfully!`,
          'success'
        );

        // Close modal and reset everything
        setShowPublishModal(false);
        setShowEmailPreview(false);
        setSelectedManager('');
        setSelectedEmployee('');
        setEmailPreviewHtml('');
      } else {
        throw new Error(result.message || 'Failed to publish assessment');
      }
    } catch (err) {
      console.error('Publish/Send Email Error:', err);
      showSnackbar(
        err.message || 'Failed to send email or publish assessment. Please try again.',
        'error'
      );
    } finally {
      setIsPublishing(false); // Always stop loading
    }

    return; // Exit early after final publish
  }

  // === PREVIEW MODE: Generate email preview only ===
  const adminEmail = sessionStorage.getItem('user_emailId') || 'admin@criticalriver.com';
  const publishedOn = new Date().toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  const managerName = selectedManager
    .split('@')[0]
    .replace('.', ' ')
    .replace(/\b\w/g, l => l.toUpperCase());

  const employeeName = selectedEmployee
    .split('@')[0]
    .replace('.', ' ')
    .replace(/\b\w/g, l => l.toUpperCase());

  const html = getSinglePublishEmailTemplate({
    managerName,
    employeeName,
    cycleCode: selectedCycle.code,
    employeeEmail: selectedEmployee,
    startDate: formatDate(selectedCycle.startDate),
    endDate: formatDate(selectedCycle.endDate),
    adminEmail,
    publishedOn,
    assessmentLink: `${window.location.origin}/assessment/${selectedCycle.id}?emp=${selectedEmployee}`
  });

  setEmailPreviewHtml(html);
  setShowEmailPreview(true);
};

const confirmPublish = async () => {
  const currentUserEmail = sessionStorage.getItem('user_emailId');

  const payload = {
    manager_email: selectedManager,
    employee_email: selectedEmployee,
    published_by: currentUserEmail,
    bulk_publish: false,
    cycle_id: selectedCycle.id
  };

  try {
    const result = await apiService.postData('/published', payload);
    if (result.status === 'success') {
      showSnackbar(`Published successfully for ${selectedEmployee}`, 'success');
      setShowPublishModal(false);
      setShowEmailPreview(false);
      setSelectedManager('');
      setSelectedEmployee('');
      setPreviewHtml('');
    } else {
      throw new Error(result.message || 'Publish failed');
    }
  } catch (err) {
    showSnackbar(err.message || 'Failed to publish', 'error');
  }
};



  // === BULK PUBLISH EMAIL PREVIEW & SEND ===
// const previewBulkEmail = async () => {
//   if (!selectedCycle) return;

//   const publishedOn = new Date().toLocaleDateString('en-US', {
//     month: 'short',
//     day: 'numeric',
//     year: 'numeric'
//   });

//   const { getBulkPublishEmailTemplate } = await import('../services/BulkPublishEmailTemplate');
//   const loginLink = "https://capabilityindex.criticalriver.com/login";

//   // Use a generic name so it's clear this is a bulk template
//   // Or show "All Employees & Managers" to make intent crystal clear
//   const previewHtml = getBulkPublishEmailTemplate({
//     recipientName: "Dear Team",  // This makes it obvious it's bulk
//     cycleCode: selectedCycle.code,
//     startDate: formatDate(selectedCycle.startDate),
//     endDate: formatDate(selectedCycle.endDate),
//     publishedOn,
//     loginLink
//   });

//   setEmailPreviewHtml(previewHtml);
//   setShowEmailPreview(true);
// };

const previewBulkEmail = async () => {
  if (!selectedCycle) return;

  let managersToUse = [];

  if (publishAllManagers) {
    managersToUse = managers;               // All managers
  } else if (selectedManagers.length > 0) {
    managersToUse = selectedManagers;       // Selected managers
  } else {
    showSnackbar('Please select at least one manager', 'error');
    return;
  }

  setIsPublishing(true);

  try {
    let allRecipients = [];

    // ---------------------------------------------
    // ALWAYS send POST with body (backend was updated)
    // ---------------------------------------------
    const resp = await apiService.postData(
      "/get-manager",
      {
        manager_emails: managersToUse ,
        cycle_id: selectedCycle.id     
      }
    );

    // ---------------------------------------------
    // Process results
    // ---------------------------------------------
    if (resp.status === "success") {
      resp.data.forEach(managerBlock => {
  managerBlock.employees
    .filter(emp => emp.has_submitted === true)  // Only include submitted employees
    .forEach(emp => {
      allRecipients.push({
        email: emp.Email,
        name: emp.FullName || emp.Email.split('@')[0],
        manager: managerBlock.manager_email
      });
    });
});
    }

    if (allRecipients.length === 0) {
      showSnackbar('No employees found', 'warning');
      return;
    }

    setRecipientList(allRecipients);
    setShowEmailPreview(true);
    setActiveTab('recipients');

    showSnackbar(
      `Loaded ${allRecipients.length} employees from ${managersToUse.length} managers`,
      'success'
    );

  } catch (err) {
    console.error(err);
    showSnackbar('Failed to load recipients', 'error');
  } finally {
    setIsPublishing(false);
  }
};

const handleBulkPublish = async () => {
  if (recipientList.length === 0) {
    showSnackbar('No recipients to publish', 'error');
    return;
  }

  setIsPublishing(true);
  let success = 0;
  let failed = 0;

  const publishedOn = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const adminEmail = sessionStorage.getItem('user_emailId') || 'admin@criticalriver.com';


  const FRONTEND_URL = 
   window._env_.REACT_APP_FRONTEND_URL || 
    window._env_.REACT_APP_APPLICATION_URL || 
    "http://localhost:3000";
  const LOGIN_URL = `${FRONTEND_URL}/login`;

  try {
    //------------------------------------------------------------------
    // ✅ 1. PREPARE LIST OF EMAILS
    //------------------------------------------------------------------
    const allEmails = recipientList.map(emp => emp.email);

    //------------------------------------------------------------------
    // ✅ 2. CREATE ONE HTML TEMPLATE FOR ALL EMPLOYEES
    //------------------------------------------------------------------
    const emailHtml = getBulkPublishEmailTemplate({
      recipientName: "All",    // group email
      cycleCode: selectedCycle.code,
      startDate: formatDate(selectedCycle.startDate),
      endDate: formatDate(selectedCycle.endDate),
      publishedOn,
      loginLink: LOGIN_URL,
      showViewButton: true
    });

    //------------------------------------------------------------------
    // ✅ 3. SEND ONE EMAIL API CALL WITH MULTIPLE RECIPIENTS
    //------------------------------------------------------------------
    await apiService.sendEmail({
      to: allEmails,   // <----- IMPORTANT
      subject: `Review Results Published - ${selectedCycle.code}`,
      html: emailHtml
    });

    //------------------------------------------------------------------
    // ⭐ 4. CONTINUE PUBLISHING EACH EMPLOYEE (NO CHANGE IN LOGIC)
    //------------------------------------------------------------------
    for (const emp of recipientList) {
      try {
        const publishPayload = {
          manager_email: emp.manager,
          employee_email: emp.email,
          published_by: adminEmail,
          bulk_publish: true,
          cycle_id: selectedCycle.id
        };

        await apiService.postData('/published', publishPayload);
        success++;

      } catch (err) {
        console.error(`Failed for ${emp.email}:`, err);
        failed++;
      }
    }

    showSnackbar(
      `Bulk Publish Complete! Success: ${success} sent, Failure: ${failed} failed`,
      failed === 0 ? 'success' : failed < success ? 'warning' : 'error'
    );

  } catch (err) {
    showSnackbar('Bulk publish failed. Please try again.', 'error');
  } finally {
    setIsPublishing(false);
    setShowBulkPublishModal(false);
    setShowEmailPreview(false);
    setRecipientList([]);
    setSelectedManagers([]);
    setPublishAllManagers(false);
  }
};


const previewSingleEmail = async () => {
  if (!selectedManager || !selectedEmployee) return;

  try {
    const resp = await apiService.postData(
      "/get-manager",
      { manager_emails: [selectedManager],cycle_id: selectedCycle.id }  
    );

    if (resp.status !== "success") {
      showSnackbar("Failed to fetch employee details", "error");
      return;
    }

    // Build email preview
    const { getSinglePublishEmailTemplate } = await import('../services/PublishEmailTemplate');
    const loginLink = "https://capabilityindex.criticalriver.com/login";

    const html = getSinglePublishEmailTemplate({
      managerName: selectedManager.split('@')[0],
      employeeName: selectedEmployee.split('@')[0],
      employeeEmail: selectedEmployee,
      cycleCode: selectedCycle.code,
      startDate: formatDate(selectedCycle.startDate),
      endDate: formatDate(selectedCycle.endDate),
      adminEmail: sessionStorage.getItem('user_emailId'),
      publishedOn: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      loginLink,
      showViewButton: false  
    });

    setEmailPreviewHtml(html);
    setShowEmailPreview(true);

  } catch (err) {
    console.error(err);
    showSnackbar("Failed to load employee info", "error");
  }
};



  // === RENDER ===
  return (
    <div className="admin-cycles-page">
      <GlobalHeader />
      <div className="content-wrapper">
        {/* Header */}
        <div className="header">
          <button onClick={() => navigate('/Home')} className="back-link">
            <span className="back-icon">←</span> Back to Home
          </button>
          <div className="header-main">
            <div>
              <h1 className="title">Assessment Cycles</h1>
              <p className="subtitle">Manage and monitor all assessment cycles across the organization</p>
            </div>
            <button onClick={() => setShowCreateModal(true)} className="create-btn">
              <Plus size={18} /> Create New Cycle
            </button>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="filter-section">
          <div className="search-wrapper">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search by cycle name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-wrapper">
            <Filter size={18} className="filter-icon" />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option>All</option>
              <option>Current</option>
              <option>Passed</option>
              <option>Planned</option>
              <option>Closed</option>
            </select>
          </div>
        </div>
        {/* <div className="stats-grid">
  <div className="stat-card total">
    <div className="stat-header">
      <span>Total Cycles</span>
      <Calendar className="icon" size={20} />
    </div>
    <p className="stat-number">{total}</p>
  </div>

  <div className="stat-card current">
    <div className="stat-header">
      <span>Current Cycle</span>
      <Clock className="icon" size={20} />
    </div>
    <p className="stat-number">{current}</p>
  </div>

  <div className="stat-card completed">
    <div className="stat-header">
      <span>Passed</span>
      <CheckCircle className="icon" size={20} />
    </div>
    <p className="stat-number">{completed}</p>
  </div>

  <div className="stat-card planned">
    <div className="stat-header">
      <span>Planned</span>
      <Calendar className="icon" size={20} />
    </div>
    <p className="stat-number">{planned}</p>
  </div>

  <div className="stat-card closed">
    <div className="stat-header">
      <span>Closed</span>
      <CheckCircle className="icon" size={20} />
    </div>
    <p className="stat-number">{closed}</p>
  </div>
</div> */}
<div className="stats-grid">
  <div 
    className="stat-card total" 
    onClick={() => setFilterStatus('All')}
    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
  >
    <div className="stat-header">
      <span>Total Cycles</span>
      <Calendar className="icon" size={20} />
    </div>
    <p className="stat-number">{total}</p>
  </div>

  <div 
    className="stat-card current" 
    onClick={() => setFilterStatus('Current')}
    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
  >
    <div className="stat-header">
      <span>Current Cycle</span>
      <Clock className="icon" size={20} />
    </div>
    <p className="stat-number">{current}</p>
  </div>

  <div 
    className="stat-card completed" 
    onClick={() => setFilterStatus('Passed')}
    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
  >
    <div className="stat-header">
      <span>Passed</span>
      <CheckCircle className="icon" size={20} />
    </div>
    <p className="stat-number">{completed}</p>
  </div>

  <div 
    className="stat-card planned" 
    onClick={() => setFilterStatus('Planned')}
    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
  >
    <div className="stat-header">
      <span>Planned</span>
      <Calendar className="icon" size={20} />
    </div>
    <p className="stat-number">{planned}</p>
  </div>

  <div 
    className="stat-card closed" 
    onClick={() => setFilterStatus('Closed')}
    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
  >
    <div className="stat-header">
      <span>Closed</span>
      <CheckCircle className="icon" size={20} />
    </div>
    <p className="stat-number">{closed}</p>
  </div>
</div>

        {/* Table */}
        <div className="table-wrapper">
          <table className="cycles-table">
            <thead>
              <tr>
                <th >#</th>
                <th >CYCLE CODE</th>
                <th >CYCLE NAME</th>
                <th >START DATE</th>
                <th >END DATE</th>
                <th >STATUS</th>
                {/* <th>PARTICIPANTS</th> */}
                <th >ACTIONS</th>
              </tr>
            </thead>
           <tbody>
  {paginatedCycles.length === 0 ? (
    <tr>
      <td colSpan="7" style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280', fontSize: '16px' }}>
        {loading ? 'Loading cycles...' : 'No cycles found matching your search.'}
      </td>
    </tr>
  ) : (
    paginatedCycles.map((cycle, index) => (
      <tr key={cycle.id}>
        <td >{(currentPage - 1) * itemsPerPage + index + 1}</td>
        <td className="bold">{cycle.code}</td>
        <td>{cycle.name}</td>
        <td>{formatDate(cycle.startDate)}</td>
        <td>{formatDate(cycle.endDate)}</td>
        <td>
          <span className={`status ${cycle.status === 'current' ? 'current' : 'passed'}`}>
            {cycle.status.toUpperCase()}
          </span>
        </td>
        <td>
          <div className="actions">
            <button className="btn-publish" onClick={() => openPublishModal(cycle)}>
              Publish
            </button>
            <button className="btn-bulk-publish" onClick={() => openBulkPublishModal(cycle)}>
              Bulk Publish
            </button>
            <button onClick={() => openEditModal(cycle)} className="btn-icon edit" title="Edit">
              Edit
            </button>
            <button onClick={() => handleDeleteClick(cycle)} className="btn-icon delete" title="Delete">
              Delete
            </button>
          </div>
        </td>
      </tr>
    ))
  )}
</tbody>
          </table>

          {/* Pagination */}
          <div className="pagination">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              First
            </button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button key={page} onClick={() => setCurrentPage(page)} className={currentPage === page ? 'active' : ''}>
                {page}
              </button>
            ))}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
              Next
            </button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
              Last
            </button>
          </div>
        </div>
      </div>

      {/* === CREATE MODAL === */}
      {/* {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3 className="modal-title">Create New Cycle</h3>
            <div className="modal-body">
              <label>Start Date</label>
              <input type="date" value={newStartDate} onChange={(e) => setNewStartDate(e.target.value)} />
              <label>End Date</label>
              <input type="date" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} />
            </div>
            <div className="modal-actions">
              <button onClick={() => { setShowCreateModal(false); setNewStartDate(''); setNewEndDate(''); }} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleCreateCycle} className="btn-primary" disabled={!newStartDate || !newEndDate}>
                Create
              </button>
            </div>
          </div>
        </div>
      )} */}
      {showCreateModal && (
  <div style={{
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '16px'
  }}     onClick={() => {
    setShowCreateModal(false);
    setSelectedCycle('');
    setNewStartDate('');
    setNewEndDate('');
    setCycleStatus('planned');
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      width: '100%',
      maxWidth: '520px',
      maxHeight: '90vh',
      overflowY: 'auto'
    }} onClick={(e) => e.stopPropagation()}>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 28px',
        borderBottom: '1px solid #e5e7eb',
        background: '#f8fafc'
      }}>
        <h3
          className="create-text"
       >
          Create New Cycle
        </h3>
        <button
          onClick={() => {
            setShowCreateModal(false);
            setSelectedCycle('');
            setNewStartDate('');
            setNewEndDate('');
            setCycleStatus('planned');
          }}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '28px',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '4px'
          }}
        >
          ×
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: '32px 28px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Cycle Dropdown */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Assessment Cycle <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <select
              value={selectedCycle}
              onChange={(e) => {
                const cycle = e.target.value;
                setSelectedCycle(cycle);
                // Extract year from cycle string (e.g., "FY2026 C1 Assessment Cycle" → 2026)
                const yearMatch = cycle.match(/FY(\d{4})/);
                const year = yearMatch ? yearMatch[1] : new Date().getFullYear();
                if (cycle.includes('C1')) {
                  setNewStartDate(`${year}-01-01`);
                  setNewEndDate(`${year}-06-30`);
                } else if (cycle.includes('C2')) {
                  setNewStartDate(`${year}-07-01`);
                  setNewEndDate(`${year}-12-31`);
                } else {
                  setNewStartDate('');
                  setNewEndDate('');
                }
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '15px',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                color: selectedCycle ? '#1f2937' : '#9ca3af'
              }}
              onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              <option value="">Select Assessment Cycle</option>
              {generateAvailableCycles().map(cycle => (
                <option key={cycle} value={cycle}>
                  {cycle}
                </option>
              ))}
            </select>
          </div>

          {/* Status Dropdown */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Status <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <select
              value={cycleStatus}
              onChange={(e) => setCycleStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '15px',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                color: '#1f2937'
              }}
              onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              <option value="planned">Planned</option>
              <option value="current">Current</option>
            </select>
          </div>

          {/* Start and End Date Fields */}
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Start Date <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={newStartDate}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '15px',
                  backgroundColor: '#f8f9fa',
                  color: '#4b5563',
                  fontWeight: '600'
                }}
              />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                End Date <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={newEndDate}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '15px',
                  backgroundColor: '#f8f9fa',
                  color: '#4b5563',
                  fontWeight: '600'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex',
        gap: '12px',
        padding: '20px 28px',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb'
      }}>
        <button
          onClick={() => {
            setShowCreateModal(false);
            setSelectedCycle('');
            setNewStartDate('');
            setNewEndDate('');
            setCycleStatus('planned');
          }}
          style={{
            flex: 1,
            padding: '14px',
            border: '2px solid #d1d5db',
            backgroundColor: 'transparent',
            color: '#374151',
            fontWeight: '600',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '15px'
          }}
        >
          Cancel
        </button>

        {/* <button
          onClick={handleCreateCycle}
          disabled={!newStartDate || !newEndDate}
          style={{
            flex: 1,
            padding: '14px',
            backgroundColor: (!newStartDate || !newEndDate) ? '#94a3b8' : '#4f46e5',
            color: 'white',
            fontWeight: '600',
            borderRadius: '12px',
            border: 'none',
            cursor: (!newStartDate || !newEndDate) ? 'not-allowed' : 'pointer',
            fontSize: '15px',
            boxShadow: (!newStartDate || !newEndDate) ? 'none' : '0 6px 16px rgba(79, 70, 229, 0.3)'
          }}
        >
          Create Cycle
        </button> */}
        <button
  onClick={handleCreateCycle}
  disabled={!selectedCycle || !cycleStatus || isPublishing}
  style={{
    flex: 1,
    padding: '14px',
    backgroundColor: (!newStartDate || !newEndDate || isPublishing) ? '#94a3b8' : '#4f46e5',
    color: 'white',
    fontWeight: '600',
    borderRadius: '12px',
    border: 'none',
    cursor: (!newStartDate || !newEndDate || isPublishing) ? 'not-allowed' : 'pointer',
    fontSize: '15px',
    boxShadow: (!newStartDate || !newEndDate || isPublishing) ? 'none' : '0 6px 16px rgba(79, 70, 229, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    opacity: isPublishing ? 0.85 : 1
  }}
>
  {isPublishing ? (
    <>
      <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.3"/>
        <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
      </svg>
      Creating cycle...
    </>
  ) : (
    'Create Cycle'
  )}
</button>
      </div>
    </div>
  </div>
)}

      {/* === EDIT MODAL === */}
      {showEditModal && editingCycle && (
  <div style={{
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '16px'
  }} onClick={() => {
    setShowEditModal(false);
    setEditingCycle(null);
    setEditStatus('');
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      width: '100%',
      maxWidth: '540px',
      maxHeight: '90vh',
      overflowY: 'auto'
    }} onClick={(e) => e.stopPropagation()}>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 28px',
        borderBottom: '1px solid #e5e7eb',
        background: '#f8fafc'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '22px',
          fontWeight: '700',
          color: '#1f2937'
        }}>
          Edit Cycle: <span style={{ color: '#4f46e5' }}>{editingCycle.code}</span>
        </h3>
        <button
          onClick={() => {
            setShowEditModal(false);
            setEditingCycle(null);
            setEditStatus('');
          }}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '28px',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '4px'
          }}
        >
          ×
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: '32px 28px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* Start Date - Disabled */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Start Date
            </label>
            <input
              type="text"
              value={formatDate(editingCycle.startDate)}
              disabled
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                backgroundColor: '#f9fafb',
                color: '#4b5563',
                fontWeight: '600',
                fontSize: '15px'
              }}
            />
          </div>

          {/* End Date - Disabled */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px'
            }}>
              End Date
            </label>
            <input
              type="text"
              value={formatDate(editingCycle.endDate)}
              disabled
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                backgroundColor: '#f9fafb',
                color: '#4b5563',
                fontWeight: '600',
                fontSize: '15px'
              }}
            />
          </div>

          {/* Status Dropdown */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Status <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <select
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '15px',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              <option value="planned">Planned</option>
              <option value="current">Current</option>
              <option value="closed">Closed</option>
            </select>
          </div>

        </div>
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex',
        gap: '12px',
        padding: '20px 28px',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb'
      }}>
        <button
          onClick={() => {
            setShowEditModal(false);
            setEditingCycle(null);
            setEditStatus('');
          }}
          style={{
            flex: 1,
            padding: '14px',
            border: '2px solid #d1d5db',
            backgroundColor: 'transparent',
            color: '#374151',
            fontWeight: '600',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '15px'
          }}
        >
          Cancel
        </button>

        {/* <button
          onClick={handleUpdateStatus}
          disabled={!editStatus}
          style={{
            flex: 1,
            padding: '14px',
            backgroundColor: !editStatus ? '#94a3b8' : '#4f46e5',
            color: 'white',
            fontWeight: '600',
            borderRadius: '12px',
            border: 'none',
            cursor: !editStatus ? 'not-allowed' : 'pointer',
            fontSize: '15px',
            boxShadow: !editStatus ? 'none' : '0 6px 16px rgba(79, 70, 229, 0.3)'
          }}
        >
          Update Status
        </button> */}
        <button
  onClick={handleUpdateStatus}
  disabled={!editStatus || isUpdatingStatus}
  style={{
    flex: 1,
    padding: '14px',
    backgroundColor: (!editStatus || isUpdatingStatus) ? '#94a3b8' : '#4f46e5',
    color: 'white',
    fontWeight: '600',
    borderRadius: '12px',
    border: 'none',
    cursor: (!editStatus || isUpdatingStatus) ? 'not-allowed' : 'pointer',
    fontSize: '15px',
    boxShadow: (!editStatus || isUpdatingStatus) ? 'none' : '0 6px 16px rgba(79, 70, 229, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  }}
>
  {isUpdatingStatus ? (
    <>
      <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.3"/>
        <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
      </svg>
      Updating...
    </>
  ) : (
    'Update Status'
  )}
</button>
      </div>
    </div>
  </div>
)}
      
{showDeleteModal && deletingCycle && (
  <div style={{
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '16px'
  }} onClick={() => setShowDeleteModal(false)}>
    
    <div style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      width: '100%',
      maxWidth: '520px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      overflow: 'hidden'
    }} onClick={(e) => e.stopPropagation()}>

      {/* Header - Red Alert Style */}
      <div style={{
        backgroundColor: '#fef2f2',
        padding: '24px 28px 20px',
        borderBottom: '1px solid #fee2e2'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#fecaca',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="3">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h3 style={{
            margin: 0,
            fontSize: '22px',
            fontWeight: '700',
            color: '#991b1b'
          }}>
            Delete Assessment Cycle
          </h3>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '28px' }}>
        <p style={{
          fontSize: '16px',
          color: '#374151',
          lineHeight: '1.6',
          margin: '0 0 24px 0'
        }}>
          Are you sure you want to <strong>permanently delete</strong> the assessment cycle?
        </p>

        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <p style={{
            margin: '0 0 8px 0',
            fontSize: '15px',
            fontWeight: '600',
            color: '#991b1b'
          }}>
            This action cannot be undone.
          </p>
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: '#7f1d1d',
            lineHeight: '1.5'
          }}>
            All data associated with this cycle, including assessments, feedback, and records will be permanently removed from the system.
          </p>
        </div>

        {/* Cycle Info */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>
            Cycle to be Deleted
          </label>
          <div style={{
            padding: '14px 18px',
            backgroundColor: '#f3f4f6',
            borderRadius: '10px',
            border: '2px solid #e5e7eb',
            fontWeight: '600',
            color: '#1f2937',
            fontSize: '15px'
          }}>
            {deletingCycle.code} — {deletingCycle.name}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowDeleteModal(false)}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: 'transparent',
              border: '2px solid #d1d5db',
              borderRadius: '12px',
              color: '#374151',
              fontWeight: '600',
              fontSize: '15px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            Cancel
          </button>

          <button
            onClick={confirmDelete}
            disabled={isDeleting}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '15px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              opacity: isDeleting ? 0.7 : 1,
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
            }}
          >
            {isDeleting ? 'Deleting...' : 'Yes, Delete Permanently'}
          </button>
        </div>
      </div>
    </div>
  </div>
)}
      {/* === PUBLISH MODAL === */}

{/* ===================== SINGLE PUBLISH MODAL WITH PREVIEW ===================== */}
{showPublishModal && selectedCycle && (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '16px'
    }}
    onClick={() => {
      setShowPublishModal(false);
      setShowEmailPreview(false);
      setSelectedManager('');
      setSelectedEmployee('');
      setEmailPreviewHtml('');
      setManagerDropdownOpen(false);
      setManagerSearchTerm('');
    }}
  >
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '100%',
        maxWidth: showEmailPreview ? '900px' : '560px',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 28px',
          borderBottom: '1px solid #e5e7eb',
          background: '#f8fafc'
        }}
      >
        <h3 className="create-text">
          {showEmailPreview ? 'Email Preview' : 'Publish Single Assessment'}
        </h3>
        <button
          onClick={() => {
            setShowPublishModal(false);
            setShowEmailPreview(false);
            setSelectedManager('');
            setSelectedEmployee('');
            setEmailPreviewHtml('');
            setManagerDropdownOpen(false);
            setManagerSearchTerm('');
          }}
          style={{ background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#6b7280' }}
        >
          ×
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: showEmailPreview ? '0' : '28px' }}>
        {!showEmailPreview ? (
          /* ========== FORM VIEW ========== */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Cycle Code */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Cycle Code
              </label>
              <input type="text" value={selectedCycle.code} disabled
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb', fontWeight: '600' }}
              />
            </div>

            {/* === MANAGER SEARCHABLE DROPDOWN === */}
            <div style={{ position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Manager Email <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div
                onClick={() => !loadingManagers && managers.length > 0 && setManagerDropdownOpen(prev => !prev)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  backgroundColor: loadingManagers || managers.length === 0 ? '#f9fafb' : 'white',
                  cursor: loadingManagers || managers.length === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '15px',
                  color: selectedManager ? '#1f2937' : '#9ca3af'
                }}
              >
                <span>
                  {loadingManagers
                    ? 'Loading managers...'
                    : managers.length === 0
                    ? 'No managers available'
                    : selectedManager || 'Select manager'}
                </span>
                {managers.length > 0 && !loadingManagers && (
                  <svg
                    style={{
                      width: '20px',
                      height: '20px',
                      transform: managerDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </div>

              {/* Manager Dropdown List */}
              {managerDropdownOpen && managers.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '2px solid #e5e7eb',
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    maxHeight: '320px',
                    overflow: 'hidden',
                    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    marginTop: '-4px'
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search manager..."
                    value={managerSearchTerm}
                    onChange={(e) => setManagerSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderBottom: '1px solid #e5e7eb',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                    autoFocus
                  />
                  <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                    {managers
                      .filter(m => m.toLowerCase().includes(managerSearchTerm.toLowerCase()))
                      .map(email => (
                        <div
                          key={email}
                          onClick={() => {
                            setSelectedManager(email);
                            setSelectedEmployee(''); // reset employee
                            fetchEmployeesForManager(email);
                            setManagerDropdownOpen(false);
                            setManagerSearchTerm('');
                          }}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            backgroundColor: selectedManager === email ? '#eff6ff' : 'transparent',
                            borderBottom: '1px solid #f3f4f6'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                          onMouseLeave={(e) =>
                            e.currentTarget.style.backgroundColor = selectedManager === email ? '#eff6ff' : 'transparent'
                          }
                        >
                          {email}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* === EMPLOYEE SEARCHABLE DROPDOWN (already perfect) === */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Employee Email <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <div
                  onClick={() => {
                    if (selectedManager && !loadingEmployees && employees.length > 0) {
                      setEmployeeSearchOpen(prev => !prev);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    backgroundColor:
                      !selectedManager || loadingEmployees || employees.length === 0
                        ? '#f9fafb'
                        : 'white',
                    cursor: selectedManager && employees.length > 0 ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '15px',
                    color: selectedEmployee ? '#1f2937' : '#9ca3af'
                  }}
                >
                  <span>
                    {loadingEmployees
                      ? 'Loading employees...'
                      : !selectedManager
                      ? 'Select a manager first'
                      : employees.length === 0
                      ? 'No employees found'
                      : selectedEmployee
                      ? employees.find(e => e.email === selectedEmployee)?.name || selectedEmployee
                      : 'Click to select employee'}
                  </span>
                  {selectedManager && employees.length > 0 && !loadingEmployees && (
                    <svg
                      style={{
                        width: '20px',
                        height: '20px',
                        transform: employeeSearchOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                      }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>

                {employeeSearchOpen && selectedManager && employees.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '2px solid #e5e7eb',
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    maxHeight: '320px',
                    overflow: 'hidden',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    marginTop: '-4px'
                  }}>
                    <div style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #e5e7eb',
                      position: 'sticky',
                      top: 0,
                      backgroundColor: 'white',
                      zIndex: 1
                    }}>
                      <input
                        type="text"
                        placeholder="Search employee..."
                        value={employeeSearchTerm}
                        onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none' }}
                        autoFocus
                      />
                    </div>
                    <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                      {filteredEmployees.length === 0 ? (
                        <div style={{ padding: '20px 16px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                          No employee found
                        </div>
                      ) : (
                        filteredEmployees.map(emp => (
                          <div
                            key={emp.email}
                            onClick={() => {
                              setSelectedEmployee(emp.email);
                              setEmployeeSearchOpen(false);
                              setEmployeeSearchTerm('');
                            }}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              backgroundColor: selectedEmployee === emp.email ? '#eff6ff' : 'transparent',
                              borderBottom: '1px solid #f3f4f6'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedEmployee === emp.email ? '#eff6ff' : 'transparent'}
                          >
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>{emp.name}</div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>{emp.email}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Published By */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Published By
              </label>
              <input
                type="text"
                value={sessionStorage.getItem('user_emailId') || 'Unknown User'}
                disabled
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f3f4f6', color: '#4b5563', fontWeight: '600' }}
              />
            </div>
          </div>
        ) : (
          /* ========== EMAIL PREVIEW ========== */
          <div style={{ backgroundColor: '#f8fafc', height: '70vh' }}>
            <iframe
              title="Email Preview"
              srcDoc={emailPreviewHtml}
              style={{ width: '100%', height: '100%', border: 'none', borderRadius: '0 0 16px 16px' }}
              sandbox="allow-same-origin"
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex',
        gap: '12px',
        padding: '20px 28px',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb'
      }}>
        <button
          onClick={() => showEmailPreview ? setShowEmailPreview(false) : setShowPublishModal(false)}
          style={{
            flex: 1,
            padding: '14px',
            border: '2px solid #d1d5db',
            backgroundColor: 'transparent',
            color: '#374151',
            fontWeight: '600',
            borderRadius: '12px',
            cursor: 'pointer'
          }}
        >
          {showEmailPreview ? 'Back' : 'Cancel'}
        </button>

        <button
          onClick={showEmailPreview ? handlePublish : previewSingleEmail}
          disabled={!selectedManager || !selectedEmployee || isPublishing}
          style={{
            flex: 1,
            padding: '14px',
            backgroundColor: (!selectedManager || !selectedEmployee || isPublishing) ? '#94a3b8' : '#2563eb',
            color: 'white',
            fontWeight: '600',
            borderRadius: '12px',
            border: 'none',
            cursor: (!selectedManager || !selectedEmployee || isPublishing) ? 'not-allowed' : 'pointer',
            boxShadow: (!selectedManager || !selectedEmployee || isPublishing) ? 'none' : '0 6px 16px rgba(37,99,235,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          {isPublishing ? (
            <>
              <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.3"/>
                <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
              </svg>
              Publishing...
            </>
          ) : (
            <>{showEmailPreview ? 'Publish & Send Email' : 'Preview Email'}</>
          )}
        </button>
      </div>
    </div>
  </div>
)}




{/* ===================== BULK PUBLISH MODAL WITH PREVIEW ===================== */}

{showBulkPublishModal && selectedCycle && (
  <div style={{
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '16px'
  }} onClick={() => {
    setShowBulkPublishModal(false);
    setShowEmailPreview(false);
    setActiveTab('recipients');
    setEmailPreviewHtml('');
    setRecipientList([]);
    setPublishAllManagers(false);
    setSelectedManagers([]);
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      width: '100%',
      maxWidth: showEmailPreview ? '1000px' : '660px',
      maxHeight: '92vh',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column'
    }} onClick={(e) => e.stopPropagation()}>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 28px',
        borderBottom: '1px solid #e5e7eb',
        background: '#f8fafc'
      }}>
        <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#1f2937' }}>
          {showEmailPreview ? 'Bulk Publish Preview' : 'Bulk Publish Cycle'}
        </h3>
        <button
          onClick={() => {
            setShowBulkPublishModal(false);
            setShowEmailPreview(false);
            setActiveTab('recipients');
            setEmailPreviewHtml('');
            setRecipientList([]);
            setPublishAllManagers(false);
            setSelectedManagers([]);
          }}
          style={{ background: 'none', border: 'none', fontSize: '32px', cursor: 'pointer', color: '#6b7280' }}
        >×</button>
      </div>

      {/* Body */}
      <div style={{ padding: '28px', flex: 1 }}>
        {!showEmailPreview ? (
          /* ========== FORM VIEW ========== */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            {/* Cycle Code */}
            <div>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Cycle Code
              </label>
              <input type="text" value={selectedCycle.code} disabled
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '12px', backgroundColor: '#f9fafb', fontWeight: '600' }} />
            </div>

            {/* All Managers */}
            <div style={{ padding: '18px', backgroundColor: '#eff6ff', borderRadius: '12px', border: '2px solid #bfdbfe' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={publishAllManagers}
                  onChange={(e) => {
                    setPublishAllManagers(e.target.checked);
                    if (e.target.checked) setSelectedManagers([]);
                  }}
                  style={{ width: '22px', height: '22px' }}
                />
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#1e40af' }}>
                  Publish for ALL Managers & Their Teams
                </span>
              </label>
            </div>

            {/* Manager Selection */}
            {!publishAllManagers && (
              <div>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                  Select Managers ({selectedManagers.length} selected)
                </label>
                <div style={{ position: 'relative' }}>
                  <div
                    onClick={() => setManagerDropdownOpen(prev => !prev)}
                    style={{
                      width: '100%', padding: '14px 16px', border: '2px solid #e5e7eb', borderRadius: '12px',
                      backgroundColor: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', fontSize: '15px'
                    }}
                  >
                    <span style={{ color: selectedManagers.length > 0 ? '#1f2937' : '#9ca3af' }}>
                      {selectedManagers.length > 0 ? `${selectedManagers.length} manager(s) selected` : 'Click to select managers'}
                    </span>
                    <svg style={{ width: '20px', height: '20px', transform: managerDropdownOpen ? 'rotate(180deg)' : '' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {managerDropdownOpen && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white',
                      border: '2px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 12px 12px',
                      maxHeight: '360px', overflow: 'hidden', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', zIndex: 1000
                    }}>
                      <input
                        type="text" placeholder="Search managers..." value={managerSearchTerm}
                        onChange={(e) => setManagerSearchTerm(e.target.value)}
                        style={{ width: '100%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontSize: '14px', outline: 'none' }}
                        autoFocus
                      />
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {managers.filter(m => m.toLowerCase().includes(managerSearchTerm.toLowerCase())).map(email => (
                          <label key={email} style={{
                            display: 'flex', alignItems: 'center', padding: '12px 16px', cursor: 'pointer',
                            backgroundColor: selectedManagers.includes(email) ? '#eff6ff' : 'transparent'
                          }}>
                            <input
                              type="checkbox" checked={selectedManagers.includes(email)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedManagers(prev => [...prev, email]);
                                else setSelectedManagers(prev => prev.filter(m => m !== email));
                              }}
                              style={{ marginRight: '12px' }}
                            />
                            <span>{email}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Published By */}
            <div>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Published By
              </label>
              <input
                type="text"
                value={sessionStorage.getItem('user_emailId') || 'Unknown User'}
                disabled
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '12px', backgroundColor: '#f3f4f6', color: '#4b5563', fontWeight: '600' }}
              />
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: '16px' }}>
              <button
                onClick={() => setActiveTab('recipients')}
                style={{
                  padding: '12px 24px', fontWeight: activeTab === 'recipients' ? '700' : '500',
                  color: activeTab === 'recipients' ? '#1f2937' : '#6b7280',
                  borderBottom: activeTab === 'recipients' ? '3px solid #4f46e5' : 'none',
                  background: 'none', cursor: 'pointer', fontSize: '16px'
                }}
              >
                Recipients ({recipientList.length})
              </button>
              <button
                onClick={() => {
                  setActiveTab('template');
                  if (!emailPreviewHtml) {
                    const html = getBulkPublishEmailTemplate({
                      recipientName: "Team Member",
                      cycleCode: selectedCycle.code,
                      startDate: formatDate(selectedCycle.startDate),
                      endDate: formatDate(selectedCycle.endDate),
                      publishedOn: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                      loginLink: window._env_.REACT_APP_FRONTEND_URL
                        ? `${window._env_.REACT_APP_FRONTEND_URL}/login`
                        : 'http://localhost:3000/login',
                      showViewButton: false
                    });
                    setEmailPreviewHtml(html);
                  }
                }}
                style={{
                  padding: '12px 24px', fontWeight: activeTab === 'template' ? '700' : '500',
                  color: activeTab === 'template' ? '#1f2937' : '#6b7280',
                  borderBottom: activeTab === 'template' ? '3px solid #4f46e5' : 'none',
                  background: 'none', cursor: 'pointer', fontSize: '16px'
                }}
              >
                Email Template
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'recipients' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ padding: '16px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px' }}>
                  <p style={{ margin: 0, fontWeight: '600', color: '#166534' }}>
                    Email will be sent to {recipientList.length} recipient(s)
                  </p>
                </div>
                <div style={{ maxHeight: '60vh', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '12px', backgroundColor: '#f9fafb' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f1f5f9' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Employee Name</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Email</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>Manager</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipientList.map((emp, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px 16px' }}>{emp.name}</td>
                          <td style={{ padding: '12px 16px', fontWeight: '600' }}>{emp.email}</td>
                          <td style={{ padding: '12px 16px', color: '#6b7280' }}>{emp.manager}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{ backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb', fontWeight: '600', fontSize: '16px' }}>
                  Email Template Preview
                </div>
                <div style={{ padding: '20px', maxHeight: '70vh', overflow: 'auto' }}>
                  <div dangerouslySetInnerHTML={{ __html: emailPreviewHtml }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex',
        gap: '12px',
        padding: '20px 28px',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb'
      }}>
        <button
          onClick={() => {
            setShowBulkPublishModal(false);
            setShowEmailPreview(false);
            setActiveTab('recipients');
            setEmailPreviewHtml('');
            setRecipientList([]);
            setPublishAllManagers(false);
            setSelectedManagers([]);
          }}
          style={{
            flex: 1,
            padding: '14px',
            border: '2px solid #d1d5db',
            backgroundColor: 'transparent',
            color: '#374151',
            fontWeight: '600',
            borderRadius: '12px',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>

        {!showEmailPreview && (
          <button
            onClick={() => {
              const html = getBulkPublishEmailTemplate({
                recipientName: "Team Member",
                cycleCode: selectedCycle.code,
                startDate: formatDate(selectedCycle.startDate),
                endDate: formatDate(selectedCycle.endDate),
                publishedOn: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                loginLink: window._env_.REACT_APP_FRONTEND_URL
                  ? `${window._env_.REACT_APP_FRONTEND_URL}/login`
                  : 'http://localhost:3000/login'
              });
              setEmailPreviewHtml(html);
              setShowEmailPreview(true);
              setActiveTab('template');
            }}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: '#6366f1',
              color: 'white',
              fontWeight: '600',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Preview Email Template
          </button>
        )}

        {!showEmailPreview && (
          <button
            onClick={previewBulkEmail}
            disabled={(!publishAllManagers && selectedManagers.length === 0) || isPublishing}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: (!publishAllManagers && selectedManagers.length === 0) || isPublishing ? '#94a3b8' : '#0891b2',
              color: 'white',
              fontWeight: '600',
              borderRadius: '12px',
              border: 'none',
              cursor: (!publishAllManagers && selectedManagers.length === 0) || isPublishing ? 'not-allowed' : 'pointer'
            }}
          >
            Preview Recipients
          </button>
        )}

        {showEmailPreview && (
          <button
            onClick={handleBulkPublish}
            disabled={isPublishing}
            style={{
              flex: 2,
              padding: '16px',
              backgroundColor: isPublishing ? '#78716c' : '#dc2626',
              color: 'white',
              fontWeight: '700',
              borderRadius: '12px',
              border: 'none',
              cursor: isPublishing ? 'not-allowed' : 'pointer',
              fontSize: '16px'
            }}
          >
            {isPublishing ? 'Sending Emails...' : `Publish & Send to ${recipientList.length} Recipients`}
          </button>
        )}

        {showEmailPreview && (
          <button
            onClick={() => setShowEmailPreview(false)}
            style={{
              padding: '14px',
              backgroundColor: '#e5e7eb',
              color: '#374151',
              fontWeight: '600',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Back
          </button>
        )}
      </div>
    </div>
  </div>
)}


      {/* === SNACKBAR === */}
      {snackbar.isOpen && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          duration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, isOpen: false }))}
        />
      )}
    </div>
  );
};

export default AdminCycleTemplate;